import Fallback from "components/Fallback/Fallback";
import detectBrowserLanguage from "detect-browser-language";
import dotenv from "dotenv";
import { SnackbarProvider } from "notistack";
import React, {
  FunctionComponent,
  lazy,
  memo,
  Suspense,
  useEffect,
} from "react";
import { IntlProvider } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { getSelectedLanguage } from "../../components/LanguageSelect/LanguageSelect";
import { setLocale } from "../../components/LanguageSelect/LanguageState";
import config from "../../config";
import messages_de from "../translations/de.json";
import messages_en from "../translations/en.json";
import messages_es from "../translations/es.json";
import messages_fr from "../translations/fr.json";
import messages_nl from "../translations/nl.json";

const Printer = lazy(() => import("../../pages/printer/Printer"));
const Router = lazy(() => import("./router"));

dotenv.config();

const messages: any = {
  de: messages_de,
  nl: messages_nl,
  en: messages_en,
  fr: messages_fr,
  es: messages_es,
};

if (config.env !== "production") {
  const log = console.error.bind(console);
  console.error = (...args: Parameters<typeof console.error>) => {
    // TODO: check periodically if these issues were fixed
    if (
      !(
        args[0] ===
          "Warning: Legacy context API has been detected within a strict-mode tree.\n\nThe old API will be supported in all 16.x releases, but applications using it should migrate to the new version.\n\nPlease update the following components: %s\n\nLearn more about this warning here: https://fb.me/react-legacy-context%s" &&
        args[1] === "Transition, TransitionGroup"
      ) &&
      !(
        args[0] ===
          "Warning: %s is deprecated in StrictMode. %s was passed an instance of %s which is inside StrictMode. Instead, add a ref directly to the element you want to reference. Learn more about using refs safely here: https://fb.me/react-strict-mode-find-node%s" &&
        args[1] === "findDOMNode"
      )
    ) {
      log(...args);
    }
  };
}

const browserLanguage = detectBrowserLanguage().split("-")[0];
const selectedLanguage = browserLanguage ?? getSelectedLanguage();

const PrinterProvider: FunctionComponent<{ children?: React.ReactNode }> = ({
  children,
}) => {
  return (
    <>
      <Switch>
        <Route path="/printing/:type/:id" component={Printer} />
        <Route>{children}</Route>
      </Switch>
    </>
  );
};

const App: FunctionComponent = () => {
  const locale = useSelector((state: any) => state.language.locale);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!!selectedLanguage) {
      dispatch(setLocale(selectedLanguage));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <div id="app-main">
      <IntlProvider
        locale={locale}
        messages={messages[locale]}
        defaultLocale="nl"
        onError={(err) => {
          if (
            err.code === "MISSING_TRANSLATION" &&
            config.env !== "production"
          ) {
            console.error(err);
          }
        }}
      >
        <SnackbarProvider maxSnack={3}>
          <Suspense fallback={<Fallback />}>
            <PrinterProvider>
              <Router />
            </PrinterProvider>
          </Suspense>
        </SnackbarProvider>
      </IntlProvider>
    </div>
  );
};

export default memo(App);
